import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useTenant } from "@/tenant/TenantProvider";
import { useOrder } from "@/store/orderStore";
import DishRow from "@/components/DishRow";
import OptionModal from "@/components/OptionModal";
import StickyCart from "@/components/StickyCart";
import PhotoOverlay from "@/components/PhotoOverlay";
import { sumMacros } from "@/lib/format";
import { TAG_GROUPS } from "@/lib/drinkTags";
import { JUMP_OFFSET, useScrollSpy } from "@/hooks/useScrollSpy";

/**
 * All drinks on one screen, like the full menu: every subcategory is a section
 * in one scroll, and the chips up top jump to it rather than filter — the list
 * never changes shape under the guest.
 */
const Drinks = ({ onBack, onViewCart }) => {
  const { state, addToCart, decrementCart } = useOrder();
  const { dishes: allDishes, drinkSubcategories, findDish } = useTenant();
  const { t } = useTranslation();
  const [photoDish, setPhotoDish] = useState(null);
  const [optDish, setOptDish] = useState(null);

  // Browsing never shows stop-listed dishes; findDish still resolves them.
  // A drink whose tag matches no group still gets rows — never silently invisible.
  const sections = useMemo(() => {
    const drinks = allDishes.filter(
      (d) => d.category === "drinks" && d.available !== false
    );
    const grouped = new Set();
    const list = drinkSubcategories
      .map((c) => {
        const tags = new Set(TAG_GROUPS[c.id] || []);
        const dishes = drinks.filter((d) => tags.has(d.tag));
        dishes.forEach((d) => grouped.add(d.id));
        return { id: c.id, emoji: c.emoji, name: c.name, short: c.short, dishes };
      })
      .filter((s) => s.dishes.length > 0);
    const stray = drinks.filter((d) => !grouped.has(d.id));
    return { list, stray };
  }, [allDishes, drinkSubcategories]);

  const sectionIds = useMemo(() => sections.list.map((s) => s.id), [sections]);
  const { jumpTo, chipClass } = useScrollSpy(sectionIds, "drinks-section-", "drinks-sub-");

  const totals = useMemo(() => sumMacros(state.cart, findDish), [state.cart, findDish]);
  const cartCount = state.cart.reduce((s, it) => s + it.qty, 0);

  const qtyMap = useMemo(() => {
    const m = {};
    state.cart.forEach((it) => {
      m[it.dishId] = (m[it.dishId] || 0) + it.qty;
    });
    return m;
  }, [state.cart]);

  const handleAdd = (dish) => {
    if (dish.milk || dish.variant) {
      setOptDish(dish);
    } else {
      addToCart(dish.id);
    }
  };
  const handleDec = (dish) => {
    const lines = state.cart.filter((it) => it.dishId === dish.id);
    if (lines.length === 0) return;
    const last = lines[lines.length - 1];
    decrementCart(last.uid);
  };

  return (
    <div className="pb-32" data-testid="drinks-screen">
      <div className="sticky top-0 z-20 bg-[#F9F9F7]/95 backdrop-blur border-b border-neutral-100">
        <div className="px-5 pt-6 pb-3 flex items-center gap-3">
          <button
            data-testid="drinks-back"
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
            aria-label={t("common.back")}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
              {t("common.menu")}
            </div>
            <div className="text-base font-bold text-neutral-900 -mt-0.5">{t("drinks.title")}</div>
          </div>
        </div>

        <div className="overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-5 pb-3 min-w-max">
            {sections.list.map((c) => (
              <button
                key={c.id}
                id={`drinks-sub-${c.id}`}
                data-testid={`drinks-sub-${c.id}`}
                onClick={() => jumpTo(c.id)}
                className={chipClass(c.id)}
              >
                <span>{c.emoji}</span> {c.short}
              </button>
            ))}
          </div>
        </div>
      </div>

      {sections.list.map((s) => (
        <div
          key={s.id}
          id={`drinks-section-${s.id}`}
          data-testid={`drinks-section-${s.id}`}
          className={`px-5 pt-5 ${JUMP_OFFSET}`}
        >
          <h2 className="text-2xl font-black tracking-tight text-neutral-900 mb-1">
            {s.emoji} {s.name}
          </h2>
          {/* "K IDR" is the price unit itself and stays put in every language —
              see lib/format.js. */}
          <p className="text-xs text-neutral-500 mb-4 font-mono uppercase tracking-wider">
            {t("build.countAndPrices", { count: s.dishes.length })}
          </p>
          <div className="bg-white border border-neutral-100 rounded-2xl px-4 shadow-sm">
            {s.dishes.map((d) => (
              <DishRow
                key={d.id}
                dish={d}
                qtyInCart={qtyMap[d.id] || 0}
                onAdd={handleAdd}
                onDec={handleDec}
                onOpenPhoto={setPhotoDish}
              />
            ))}
          </div>
        </div>
      ))}

      {sections.stray.length > 0 && (
        <div className="px-5 pt-5">
          <div className="bg-white border border-neutral-100 rounded-2xl px-4 shadow-sm">
            {sections.stray.map((d) => (
              <DishRow
                key={d.id}
                dish={d}
                qtyInCart={qtyMap[d.id] || 0}
                onAdd={handleAdd}
                onDec={handleDec}
                onOpenPhoto={setPhotoDish}
              />
            ))}
          </div>
        </div>
      )}

      {photoDish && <PhotoOverlay dish={photoDish} onClose={() => setPhotoDish(null)} />}

      {optDish && (
        <OptionModal
          dish={optDish}
          onClose={() => setOptDish(null)}
          onConfirm={(opts) => {
            addToCart(optDish.id, opts);
            setOptDish(null);
          }}
        />
      )}

      <StickyCart totals={totals} cartCount={cartCount} onView={onViewCart} />
    </div>
  );
};

export default Drinks;
